import { useCallback, useState } from "react";
import { AgGrid } from "@jsq/ds/core";
import { RowItem as ComplianceClientRow } from "../ComplianceClientsTable/types";
import { ComplianceEntityRow } from "../ComplianceEntitiesTable/types";
import { SettingsDrawerRow, SettingsDrawerTab } from "./types";

export const tabForEntitiesColumn = (colDef?: {
  colId?: string;
  field?: string;
}): SettingsDrawerTab => {
  const id = colDef?.colId ?? colDef?.field;
  return id === "entityProgram"
    ? SettingsDrawerTab.EntityProgram
    : SettingsDrawerTab.InvestorProgram;
};

type SelectedClient = {
  /** Arena db id of the client picked in the grid's client search. */
  arenaId: number | null;
  clientName?: string;
};

/** Cell-click state for the entities settings drawer: open while a row is selected. */
export const useComplianceSettingsDrawer = ({ arenaId, clientName }: SelectedClient) => {
  const [row, setRow] = useState<SettingsDrawerRow | undefined>(undefined);
  const [defaultTab, setDefaultTab] = useState<SettingsDrawerTab>(
    SettingsDrawerTab.InvestorProgram,
  );

  const onCellClicked = useCallback(
    (event: AgGrid.CellClickedEvent<ComplianceEntityRow>) => {
      const data = event.data;
      if (!data || arenaId === null) return;
      // The onboarding ⋯ menu handles its own clicks (AG Grid's cell-click fires before React,
      // so we skip by target rather than stopPropagation).
      const target = (event.event as MouseEvent | undefined)?.target;
      if (target instanceof Element && target.closest("[data-suppress-row-click]")) {
        return;
      }
      setDefaultTab(tabForEntitiesColumn(event.colDef));
      setRow({
        id: String(data.id),
        arenaId: String(arenaId),
        name: data.name,
        arenaName: clientName,
        complianceProgram: data.complianceProgram ?? undefined,
        complianceEnabled: data.complianceEnabled,
        isInvestorDiligenceEnabled: data.isInvestorDiligenceEnabled,
        entityProgram: data.entityProgram ?? undefined,
        fatcaCrsEnabled: data.fatcaCrsEnabled,
      });
    },
    [arenaId, clientName],
  );

  const onClose = useCallback(() => {
    setRow(undefined);
    setDefaultTab(SettingsDrawerTab.InvestorProgram);
  }, []);

  return { row, defaultTab, onCellClicked, onClose };
};

/** Client-grid variant: a client row *is* the arena, so no client-search context. */
export const useComplianceClientSettingsDrawer = () => {
  const [row, setRow] = useState<SettingsDrawerRow | undefined>(undefined);

  const onRowClicked = useCallback((event: AgGrid.RowClickedEvent<ComplianceClientRow>) => {
    const data = event.data;
    if (!data) return;
    // The Entities-count launch link and the onboarding ⋯ menu handle their own clicks.
    const target = (event.event as MouseEvent | undefined)?.target;
    if (
      target instanceof Element &&
      target.closest("a, button, [role='button'], [data-suppress-row-click]")
    ) {
      return;
    }
    setRow({
      id: String(data.id),
      arenaId: String(data.id),
      name: data.client,
      clientProgram: data.clientProgram,
    });
  }, []);

  const onClose = useCallback(() => setRow(undefined), []);

  return { row, onRowClicked, onClose };
};
